/**
 * Pixel install — the single most important screen for a standalone account.
 *
 * Until a purchase event arrives we have no revenue data, which means the
 * optimiser has nothing to optimise against and the honest-attribution
 * positioning is vapour. So this screen does not congratulate the customer for
 * pasting the first snippet: it stays visibly incomplete until a real purchase
 * lands, and says plainly what is missing and why it matters.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, fmt, type PixelInstall, type PixelStatus } from '../api';

export default function PixelSetup() {
  const [install, setInstall] = useState<PixelInstall | null>(null);
  const [status, setStatus] = useState<PixelStatus | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const poll = useCallback(() => {
    api.pixelStatus().then(setStatus).catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .pixelInstructions()
      .then(setInstall)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load your pixel.'));
    poll();
    // The customer usually has this page open in one tab and their own site in
    // another, so polling is exactly the right interaction here.
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [poll]);

  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Could not copy — select the code and copy it manually.');
    }
  }

  if (error && !install) return <div className="empty">{error}</div>;
  if (!install) return <div className="empty">Loading your pixel…</div>;

  const step1Done = Boolean(status?.installed);
  const step2Done = Boolean(status?.verified);

  return (
    <>
      <p className="eyebrow">Setup · required</p>
      <h1 className="pg-title">Install your pixel</h1>
      <p className="pg-sub">
        This is how ScaleAds AI measures revenue. Without it we can see what you spend but not what you
        earn — and an optimiser that can only see spend will happily scale a campaign that has sold
        nothing. Two snippets, both copy-paste.
      </p>

      <div className={`card card-p setup-status ${step2Done ? 'done' : ''}`}>
        <h4>
          Status
          <span className="meta">checks every 5 seconds</span>
        </h4>
        <ol className="setup-steps">
          <li className={step1Done ? 'done' : 'active'}>
            <span className="tick">{step1Done ? '✓' : '1'}</span>
            <div>
              <strong>Pixel receiving page views</strong>
              <em>
                {step1Done
                  ? `${fmt.num(status!.pageViews24h)} in the last 24 hours`
                  : 'Waiting for the first event'}
              </em>
            </div>
          </li>
          <li className={step2Done ? 'done' : step1Done ? 'active' : ''}>
            <span className="tick">{step2Done ? '✓' : '2'}</span>
            <div>
              <strong>Purchase event confirmed</strong>
              <em>
                {step2Done
                  ? `${fmt.num(status!.purchases24h)} purchases seen in the last 24 hours`
                  : 'Not yet received — this is the one that matters'}
              </em>
            </div>
          </li>
        </ol>

        {status?.blocker && <p className="warn-line">{status.blocker}</p>}

        {step2Done && (
          <p className="ok-line">
            Working. Revenue is flowing in and the optimiser has something to learn from.
          </p>
        )}
      </div>

      <div className="card card-p">
        <h4>
          Step 1 — every page
          <span className="meta">
            paste in <code>&lt;head&gt;</code>
          </span>
        </h4>
        <p className="pg-sub" style={{ marginBottom: 12 }}>
          Loads nothing from our servers and adds no render-blocking request. Your pixel id is{' '}
          <code>{install.pixelId}</code>.
        </p>
        <pre className="code-block">{install.snippet}</pre>
        <button className="btn sm brand" onClick={() => void copy(install.snippet, 'base')}>
          {copied === 'base' ? 'Copied' : 'Copy snippet'}
        </button>
      </div>

      <div className="card card-p">
        <h4>
          Step 2 — order confirmation page
          <span className="meta">the one people forget</span>
        </h4>
        <p className="pg-sub" style={{ marginBottom: 12 }}>
          Fire this once, after a successful order, with your real values.{' '}
          <strong>value_cents is an integer in cents</strong> — $59.00 is <code>5900</code>, not{' '}
          <code>59</code>. Getting that wrong misreports your revenue by 100×, and every
          recommendation we make afterwards inherits the error.
        </p>
        <pre className="code-block">{install.purchaseSnippet}</pre>
        <button className="btn sm brand" onClick={() => void copy(install.purchaseSnippet, 'buy')}>
          {copied === 'buy' ? 'Copied' : 'Copy snippet'}
        </button>
        <p className="footnote">{install.gtmNote}</p>
      </div>

      <div className="card card-p">
        <h4>What we collect</h4>
        <table className="data">
          <tbody>
            <tr>
              <td>Page URL and referrer</td>
              <td className="meta">attribution — which ad drove the visit</td>
            </tr>
            <tr>
              <td>UTM parameters</td>
              <td className="meta">matches a visit to a specific campaign and creative</td>
            </tr>
            <tr>
              <td>Order id and value</td>
              <td className="meta">revenue, and de-duplication against platform pixels</td>
            </tr>
            <tr>
              <td>A random visitor id</td>
              <td className="meta">stored in localStorage; not linked to a person</td>
            </tr>
          </tbody>
        </table>
        <p className="footnote">
          No names, no email addresses, no payment details — the snippet has no access to them and
          we do not want them. If you send us an email address for advanced matching, it is hashed
          before it leaves the browser. Add ScaleAds AI to your privacy policy alongside your other
          analytics tools.
        </p>
      </div>
    </>
  );
}
